import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	CreatorAccessory,
	CreatorInitialData,
	CreatorOptions,
	CreatorRendererOptions,
	CreatorState,
	CreatorStep,
} from '@/types/creator';
import {
	AccessorySlot,
	AccessoryTranslationType,
	AxisIndex,
	ThreeEvent,
	ThreeEvents,
} from '@/renderer/types';
import { ProductTypeVariation } from '@/api/ProductTypeVariationApi';
import { Product } from '@/api/ProductApi';
import {
	getAvailableAttributeValues,
	getInitialSelectedValues,
} from '@/utils/utilities';
import { AttributeValue } from '@/api/AttributeValueApi';
import { UUID } from '@/types/types';
import { omit, remove } from 'lodash';
import { AccessoryRelationAction } from '@/api/AccessoryApi';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import APP_CONFIG from '@/configs/appConfig';

const initialState: CreatorState = {
	product: null,
	productType: null,
	productTypeVariations: [],
	availableAccessories: [],
	availableAttributeValues: {
		attributes: [],
		dimensionAttributes: [],
	},
	productTypeVariation: null,
	renderer: {
		loading: false,
		distance: RENDERER_CONFIG.INITIAL_DISTANCE,
		background: APP_CONFIG.DEFAULT_BACKGROUND,
	},
	selectedValues: {},
	creatorAccessories: [],
	comment: '',
	quantity: 1,
	options: {
		initialized: false,
		holdInitialize: !!localStorage.getItem(
			APP_CONFIG.AUTO_SAVE.TEMP_PROJECT_KEY
		),
		accessoriesDrawer: {
			opened: false,
			categoryId: null,
		},
		selectedCreatorAccessoryId: null,
		slotsData: {},
		currentStep: CreatorStep.ATTRIBUTES,
		infoDrawer: null,
	},
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		initialize: (state, { payload }: PayloadAction<CreatorInitialData>) => {
			setAccessoriesAvailability(state, () => {
				state.options.initialized = true;

				state.productType = payload.productType;
				state.productTypeVariations = payload.productTypeVariations;
				state.availableAttributeValues = getAvailableAttributeValues(
					state.productType
				);
				state.availableAccessories = payload.productType.accessories.map(
					(accessory) => ({
						...accessory,
						disabled: false,
					})
				);

				state.selectedValues = getInitialSelectedValues(
					payload.productType,
					payload.defaultVariation
				);
			});
		},
		loadProduct: (
			state,
			{
				payload,
			}: PayloadAction<{
				initialData: CreatorInitialData;
				product: Product;
			}>
		) => {
			setAccessoriesAvailability(state, () => {
				state.options.initialized = true;
				const { initialData, product } = payload;

				const creatorParams = product.options;

				// if no prod.id => configuration loaded from local
				if (product.id) {
					state.product = product;
					state.quantity = product.quantity;
					state.comment = product.comment;
				}

				state.productType = initialData.productType;
				state.productTypeVariations = initialData.productTypeVariations;

				const productTypeVariation = state.productTypeVariations.find(
					(variation) => variation.id === product.productTypeVariationId
				);

				if (!productTypeVariation)
					throw new Error('MISSING_PRODUCT_TYPE_VARIATION');

				state.productTypeVariation = productTypeVariation;
				state.availableAttributeValues = getAvailableAttributeValues(
					state.productType
				);

				let attributeId: UUID;
				for (attributeId in creatorParams.selectedValues) {
					const value = creatorParams.selectedValues[attributeId];
					if (!value) {
						{
							state.selectedValues[attributeId] = null;
							continue;
						}
					}

					const attributeValue = initialData.productType.attributeValues.find(
						(attributeValue) => attributeValue.id === value
					);

					if (!attributeValue) throw new Error('MISSING_ATTRIBUTE_VALUE');

					state.selectedValues[attributeId] = attributeValue;
				}

				state.availableAccessories = initialData.productType.accessories.map(
					(accessory) => ({
						...accessory,
						disabled: false,
					})
				);

				state.creatorAccessories = product.options.creatorAccessories.map(
					(partialCreaAccessory) => {
						const accessory = initialData.productType.accessories.find(
							(accessory) => accessory.id === partialCreaAccessory.accessoryId
						);
						if (!accessory) throw new Error('MISSING_ACCESSORY');

						return {
							...omit(partialCreaAccessory, ['accessoryId']),
							accessory,
						};
					}
				);
			});
		},
		selectVariation: (
			state,
			{
				payload,
			}: PayloadAction<{
				variation: ProductTypeVariation;
				resetAccessories: boolean;
			}>
		) => {
			setAccessoriesAvailability(state, () => {
				state.productTypeVariation = payload.variation;

				if (payload.resetAccessories) state.creatorAccessories = [];
			});
		},
		selectAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				attributeId: UUID;
				attributeValue: AttributeValue;
			}>
		) => {
			state.selectedValues[payload.attributeId] = payload.attributeValue;
		},
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.DISTANCE:
					state.renderer.distance =
						payload.data as ThreeEvents[ThreeEvent.DISTANCE];
					break;
				case ThreeEvent.SET_SLOTS_DATA:
					state.options.slotsData =
						payload.data as ThreeEvents[ThreeEvent.SET_SLOTS_DATA];
					break;
				case ThreeEvent.SET_ACCESSORIES_IS_COLLIDING: {
					const data =
						payload.data as ThreeEvents[ThreeEvent.SET_ACCESSORIES_IS_COLLIDING];

					for (const rendererAccessory of data) {
						const creatorAccessory = state.creatorAccessories.find(
							(accessory) => accessory.refId === rendererAccessory.refId
						);
						if (!creatorAccessory) continue;
						creatorAccessory.isColliding = rendererAccessory.isColliding;
					}

					break;
				}
			}
		},
		addCreatorAccessory: (
			state,
			{
				payload,
			}: PayloadAction<
				Pick<CreatorAccessory, 'accessory' | 'refId' | 'isTemporary'>
			>
		) => {
			setAccessoriesAvailability(state, () => {
				const slotsData = state.options.slotsData[payload.accessory.slug];
				if (!slotsData) throw new Error('MISSING_SLOTS_DATA_FOR_ACCESSORY');

				const accessoriesUpperBoundLimitY = state.creatorAccessories.find(
					(creatorAccessory) =>
						creatorAccessory.slotsData.accessoriesUpperBoundLimitY !== undefined
				)?.slotsData.accessoriesUpperBoundLimitY;

				const axesPositions = slotsData.initialPosition;

				if (accessoriesUpperBoundLimitY !== undefined)
					axesPositions[AxisIndex.Y] = Math.min(
						axesPositions[AxisIndex.Y],
						accessoriesUpperBoundLimitY
					);

				const creatoAccessory: CreatorAccessory = {
					...payload,
					slotsData,
					isColliding: false,
					selectedSlots: [null, null, null],
					axesPositions,
				};

				slotsData.translationAxes.forEach((translationType, idx) => {
					if (translationType !== AccessoryTranslationType.SLOTS) return;
					const defaultSlot = slotsData.defaultSlots[idx];

					creatoAccessory.selectedSlots[idx] = defaultSlot;

					if (defaultSlot)
						creatoAccessory.axesPositions[defaultSlot.axis] =
							defaultSlot.position[defaultSlot.axis];
				});

				state.creatorAccessories.push(creatoAccessory);
				state.options.selectedCreatorAccessoryId = creatoAccessory.refId;
			});
		},
		setCreatorAccessorySlot: (
			state,
			{
				payload,
			}: PayloadAction<{
				refId: UUID;
				slot: AccessorySlot;
				axis: AxisIndex;
			}>
		) => {
			for (const creatorAccessory of state.creatorAccessories) {
				if (creatorAccessory.refId !== payload.refId) continue;
				creatorAccessory.selectedSlots[payload.axis] = payload.slot;

				break;
			}
		},
		setCreatorAccessoryAxesPositions: (
			state,
			{
				payload,
			}: PayloadAction<{
				refId: UUID;
				axis: AxisIndex;
				position: number;
			}>
		) => {
			for (const creatorAccessory of state.creatorAccessories) {
				if (creatorAccessory.refId !== payload.refId) continue;
				creatorAccessory.axesPositions[payload.axis] = payload.position;

				break;
			}
		},
		updateCreatorAccessory: (
			state,
			{
				payload,
			}: PayloadAction<{
				refId: UUID;
				data: Partial<CreatorAccessory>;
			}>
		) => {
			for (const accessory of state.creatorAccessories) {
				if (accessory.refId !== payload.refId) continue;
				Object.assign(accessory, payload.data);
				break;
			}
		},
		removeCreatorAccessory: (state, { payload }: PayloadAction<UUID>) => {
			setAccessoriesAvailability(state, () => {
				remove(
					state.creatorAccessories,
					(accessory) => accessory.refId === payload
				);
			});
		},
		setBackground: (state, { payload }: PayloadAction<string>) => {
			state.renderer.background = payload;
		},
		setCreatorStep: (state, { payload }: PayloadAction<'prev' | 'next'>) => {
			payload === 'prev'
				? state.options.currentStep--
				: state.options.currentStep++;
		},
		reset: (state) => initialState,
	},
});

function setAccessoriesAvailability(
	state: CreatorState,
	updater: (state: CreatorState) => void
) {
	updater(state);

	for (const accessory of state.availableAccessories) {
		if (!accessory.relations?.length) {
			accessory.disabled = false;
			continue;
		}

		accessory.disabled = accessory.relations.some((relation) => {
			switch (relation.action) {
				case AccessoryRelationAction.HIDE:
					return relation.accessories.some((id) =>
						state.creatorAccessories.some(
							(creatorAccessory) => creatorAccessory.accessory.id === id
						)
					);
				case AccessoryRelationAction.SHOW:
					return !relation.accessories.some((id) =>
						state.creatorAccessories.some(
							(creatorAccessory) => creatorAccessory.accessory.id === id
						)
					);
			}
		});
	}

	return;
}

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
